import React, { useEffect, useState } from "react";
import CustomContainer from "../common/CustomContainer";
import BillingTabs from "./BillingTabs";
import BillingDetailsCard from "./BillingDetailsCard";
import BillingService from "./billing-service";
import { useTheme } from "../../ThemeContext";
import { Box, Typography } from "@mui/material";
import PlansAndLimits from "./PlansAndLimits";
import LogoLoader from "../common/loaders/gif-logo-loader";
import TransactionSuccess from "../common/loaders/transaction-success";
import Usage from "./Usage";

const MAX_WIDTH = 1800;

const Billing = () => {
  const { currentTheme, themeMode } = useTheme();
  // Helper to get query param
  function getTabFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam === "usage" || tabParam === "plans" || tabParam === "settings") return tabParam;
    return "usage";
  }
  const [tab, setTab] = useState(getTabFromUrl());
  const [billingData, setBillingData] = useState({
    plan: null,
    units: { used: 0, total: 0, isOverageActive: false, overagePlanType: null, planType: null, totalUsage: 0, includedUnitsLimit: 0, overageRatePerHour: null },
    paymentMethod: null,
    billingInfo: null,
    loading: true,
  });
  const [error, setError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [showTransactionSuccess, setShowTransactionSuccess] = useState(false);

  const handleAddPaymentMethod = async () => {
    setPortalLoading(true);
    try {
      const returnUrl = window.location.origin + "/billing";
      const result = await BillingService.createPortalSession(returnUrl);
      if (result.status === "SUCCESS" && result.url) {
        window.location.href = result.url;
      } else {
        setError("Could not create portal session.");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleEditBillingInfo = async () => {
    try {
      const returnUrl = window.location.origin + "/billing";
      // Pass a section param to indicate the user wants to edit billing info
      const result = await BillingService.createPortalSession(
        returnUrl,
        "other"
      );
      if (result.status === "SUCCESS" && result.url) {
        window.location.href = result.url;
      } else {
        setError("Could not create portal session.");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  //   useEffect(() => {
  //     toggleTheme();
  //   }, []);

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const staffInfo = JSON.parse(sessionStorage.getItem("staff_info"));
        const email = staffInfo?.email;
        if (!email) {
          setError("No staff email found.");
          setBillingData((prev) => ({ ...prev, loading: false }));
          return;
        }

        // Check for Stripe checkout success
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get('session_id');
        const checkoutStatus = params.get('checkout');
        
        if (checkoutStatus === 'success' && sessionId) {
          // Finalize the subscription
          try {
            const finalizeResult = await BillingService.finalizeSubscription(sessionId);
            if (finalizeResult.status === 'SUCCESS') {
              // Show success message for 3 seconds
              setShowTransactionSuccess(true);
              setTimeout(() => {
                setShowTransactionSuccess(false);
              }, 3000);
            } else {
              console.error('Failed to finalize subscription:', finalizeResult);
              setError(finalizeResult.response?.message || finalizeResult.message || 'Failed to finalize subscription');
            }
          } catch (error) {
            console.error('Error finalizing subscription:', error);
            if (error.response) {
              console.error('Response data:', error.response.data);
              console.error('Response status:', error.response.status);
              console.error('Response headers:', error.response.headers);
            }
            setError(error.message || 'Failed to finalize subscription');
          }
          // Remove the checkout parameters from the URL
          params.delete('session_id');
          params.delete('checkout');
          window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
        }

        // Ensure customer exists (create if not)
        const initResult = await BillingService.initializeCustomer(email);
        if (!initResult.success) {
          setError(initResult.message || "Failed to initialize customer");
          setBillingData((prev) => ({ ...prev, loading: false }));
          return;
        }

        // Fetch units data
        const unitsResult = await BillingService.getRemainingUnits(email);
        if (!unitsResult.success) {
          setError(unitsResult.message || "Failed to fetch usage");
          setBillingData((prev) => ({ ...prev, loading: false }));
          return;
        }

        // Fetch settings data
        const settingsResult = await BillingService.getCustomerSettings(email);
        if (!settingsResult.success) {
          setError(
            settingsResult.message || "Failed to fetch billing settings"
          );
          setBillingData((prev) => ({ ...prev, loading: false }));
          return;
        }

        setBillingData({
          plan: settingsResult.plan,
          units: {
            used: unitsResult.totalUsage ?? 0,
            total: unitsResult.includedUnitsLimit ?? 0,
            isOverageActive: unitsResult.isOverageActive ?? false,
            overagePlanType: unitsResult.overagePlanType ?? null,
            planType: unitsResult.planType ?? null,
            totalUsage: unitsResult.totalUsage ?? 0,
            includedUnitsLimit: unitsResult.includedUnitsLimit ?? 0,
            overageRatePerHour: unitsResult.overageRatePerHour ?? null,
          },
          paymentMethod: settingsResult.paymentMethod,
          billingInfo: settingsResult.billingInfo,
          loading: false,
        });
      } catch (e) {
        setError("Unexpected error");
        setBillingData((prev) => ({ ...prev, loading: false }));
      }
    };
    fetchBillingData();
  }, []);

  // Update URL when tab changes
  const handleTabChange = (_, v) => {
    setTab(v);
    const params = new URLSearchParams(window.location.search);
    params.set("tab", v);
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${params.toString()}`
    );
  };

  return (
    <>
      {showTransactionSuccess && <TransactionSuccess />}
      {!showTransactionSuccess && (
        <CustomContainer containerWidth="90%">
          <Box
            sx={{
              maxWidth: MAX_WIDTH,
              mx: "auto",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 4,
              "& > *": {
                width: "100%",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "0px",
                  height: "8px",
                  background: "transparent",
                },
                "&::-webkit-scrollbar-track": {
                  background: themeMode === "dark" ? "#2d2d2d" : "#f1f1f1",
                  borderRadius: "4px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: themeMode === "dark" ? "#555" : "#888",
                  borderRadius: "4px",
                  "&:hover": {
                    background: themeMode === "dark" ? "#666" : "#999",
                  },
                },
                "&:not(:hover)::-webkit-scrollbar-thumb": {
                  background: "transparent",
                },
              },
            }}
          >
            <Box>
              {billingData.loading ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '400px' 
                  }}
                >
                  <LogoLoader />
                </Box>
              ) : error ? (
                <div
                  style={{
                    color: currentTheme.common.danger,
                    textAlign: "center",
                    marginTop: 40,
                  }}
                >
                  {error}
                </div>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mb: 2,
                    }}
                  >
                    <BillingTabs value={tab} onChange={handleTabChange} />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      color: themeMode === "dark" ? "#ffffff" : "#000000",
                      opacity: 0.7,
                      fontSize: "0.95rem",
                      mb: 2,
                    }}
                  >
                    Manage your subscription, view usage, and explore available plans
                  </Typography>
                  {tab === "usage" ? (
                    <Usage
                      units={billingData.units}
                      setBillingData={setBillingData}
                      maxWidth={MAX_WIDTH}
                    />
                  ) : tab === "settings" ? (
                    <BillingDetailsCard
                      planName={billingData.plan?.name}
                      units={billingData.units}
                      paymentMethod={billingData.paymentMethod}
                      onAddPaymentMethod={handleAddPaymentMethod}
                      billingInfo={billingData.billingInfo}
                      onEditBillingInfo={handleEditBillingInfo}
                      textColor={themeMode === 'dark' ? '#ffffff' : "#000000"}
                      bg={currentTheme.callhistory.bgLite}
                      portalLoading={portalLoading}
                      themeMode={themeMode}
                    />
                  ) : (
                    <PlansAndLimits
                      usage={billingData.units}
                      planName={billingData.plan?.name}
                      email={JSON.parse(sessionStorage.getItem("staff_info"))?.email}
                    />
                  )}
                </>
              )}
            </Box>
          </Box>
        </CustomContainer>
      )}
    </>
  );
};

export default Billing;
